import React, { useEffect, useRef, useState, useImperativeHandle } from "react";
import ReactPlayer from "react-player";
import "../assets/SignagePlayer.css";
import { Image } from "antd";

const SignagePlayer = React.forwardRef(({ playlist = [], classNames, enableControls = false }, ref) => {
  const playerRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [videoUrl, setVideoUrl] = useState("");
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);

  const currentMedia = playlist && playlist[currentIndex];

  // The admin preview modals (PreviewPlaylist/PreviewMedia) render this
  // component into a small fixed-size box (see SignagePlayer.css's
  // .preview-media.horizontal/.vertical - 400x225 / 225x400) via this
  // classNames prop; the real full-screen player (Play.jsx) doesn't pass
  // it at all. A "url" iframe sized to that small box's actual pixel width
  // (via width="100%") gives embedded sites a viewport narrow enough to
  // trigger their own mobile/responsive breakpoint - it renders correctly,
  // just not as "what this looks like on the actual TV". Rendering the
  // iframe at a real screen-sized viewport and scaling the whole thing
  // down to fit the small box (rather than shrinking the iframe's own
  // width) keeps the embedded page seeing a normal desktop/TV-sized
  // viewport, so it lays out the same way it would on the real signage
  // screen.
  const isPreview = !!classNames && classNames.includes("preview-media");
  const isPortrait = !!classNames && classNames.includes("vertical");
  const PREVIEW_BOX = isPortrait
    ? { width: 225, height: 400 }
    : { width: 400, height: 225 };
  const VIRTUAL_SCREEN = isPortrait
    ? { width: 1080, height: 1920 }
    : { width: 1920, height: 1080 };
  const urlPreviewScale = PREVIEW_BOX.width / VIRTUAL_SCREEN.width;

  const nextMedia = () => {
    if (
      playlist.length === 1 &&
      (currentMedia.media.media_type === "youtube" ||
        currentMedia.media.media_type === "video")
    ) {
      playerRef.current.seekTo(0, "seconds");
    }
    setCurrentIndex((prev) => (prev + 1) % playlist.length);
  };

  const prevMedia = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? playlist.length - 1 : prev - 1
    );
  };

  useImperativeHandle(ref, () => ({
    nextMedia,
    prevMedia,
  }));

  useEffect(() => {
    if (currentMedia) {
      if (
        currentMedia.media.media_type === "video" ||
        currentMedia.media.media_type === "image" ||
        currentMedia.media.media_type === "ppt"
      ) {
        const file_data = currentMedia.media.file_data;
        setVideoUrl(file_data.file_path);
      } else if (
        currentMedia.media.media_type === "youtube" ||
        currentMedia.media.media_type === "url"
      ) {
        setVideoUrl(currentMedia.media.url_data);
      }
      if (
        currentMedia.media.media_type === "image" ||
        currentMedia.media.media_type === "ppt" ||
        currentMedia.media.media_type === "url"
      ) {
        // "url" has no ReactPlayer/onEnded signal to advance on (it's a
        // plain iframe embed, see the render block below) - same
        // duration-timer treatment as image/ppt.
        const timer = setTimeout(
          nextMedia,
          currentMedia.duration * 1000 || 5000
        );
        return () => clearTimeout(timer);
      }
      console.log(
        "Playing " +
        currentMedia.media.media_type +
        " - " +
        currentMedia.media.title
      );
    }
  }, [currentIndex, currentMedia]);

  useEffect(() => {
    const handleResize = () => {
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={`${classNames} signage-wrapper`}>
      {(currentMedia.media.media_type === "video" ||
        currentMedia.media.media_type === "youtube") && (
          <ReactPlayer
            config={{
              youtube: {
                playerVars: {
                  controls: enableControls ? 1 : 0,
                  modestbranding: 1,
                  rel: 0,
                  showinfo: 0,
                },
              },
            }}
            ref={playerRef}
            url={videoUrl}
            playing
            muted={isMuted}
            loop={false}
            controls={enableControls}
            width="100%"
            height="100%"
            onEnded={nextMedia}
            onReady={() => {
              const ytPlayer = playerRef.current?.getInternalPlayer();
              if (
                currentMedia.media.media_type === "youtube" &&
                !isMuted &&
                ytPlayer?.unMute
              ) {
                ytPlayer.unMute(); // Force unmute
              }
            }}
            onPlay={() => {
              const ytPlayer = playerRef.current?.getInternalPlayer();
              if (
                currentMedia.media.media_type === "youtube" &&
                ytPlayer?.isMuted &&
                typeof ytPlayer.isMuted === "function" &&
                ytPlayer.isMuted() === false
              ) {
                setIsMuted(false); // Save the unmuted state globally
              }
            }}
          />
        )}
      {currentMedia.media.media_type === "image" && (
        <div className="text-center bg-black">
          <Image src={videoUrl} height={"100vh"} preview={false} />
        </div>
      )}
      {currentMedia.media.media_type === "ppt" && (
        <div className="text-center bg-black">
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
              videoUrl
            )}&start=true&loop=true&delayms=3000`}
            width="100%"
            height={`${screenHeight}px`}
            frameBorder="0"
            title={currentMedia.media.title}
          ></iframe>
        </div>
      )}
      {currentMedia.media.media_type === "url" && (
        isPreview ? (
          <div
            className="text-center bg-black"
            style={{
              width: PREVIEW_BOX.width,
              height: PREVIEW_BOX.height,
              overflow: "hidden",
            }}
          >
            <iframe
              src={videoUrl}
              width={VIRTUAL_SCREEN.width}
              height={VIRTUAL_SCREEN.height}
              frameBorder="0"
              allow="autoplay; encrypted-media; fullscreen"
              title={currentMedia.media.title}
              style={{
                transform: `scale(${urlPreviewScale})`,
                transformOrigin: "top left",
              }}
            ></iframe>
          </div>
        ) : (
          <div className="text-center bg-black">
            <iframe
              src={videoUrl}
              width="100%"
              height={`${screenHeight}px`}
              frameBorder="0"
              allow="autoplay; encrypted-media; fullscreen"
              title={currentMedia.media.title}
            ></iframe>
          </div>
        )
      )}
    </div>
  );
});

export default SignagePlayer;
